import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { EndeavorTypeApi } from '../../services/ApiService';

function EndeavorTypeMetadataList() {
  const { id: endeavorTypeId } = useParams();
  const navigate = useNavigate();
  const [endeavorType, setEndeavorType] = useState(null);
  const [metadataDefinitions, setMetadataDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load endeavor type details
      const typeResponse = await EndeavorTypeApi.getEndeavorType(endeavorTypeId);
      if (!typeResponse.ok) {
        throw new Error('Failed to load endeavor type');
      }
      const typeData = await typeResponse.json();
      setEndeavorType(typeData);

      // Load metadata definitions for this endeavor type
      const metadataResponse = await EndeavorTypeApi.getEndeavorTypeMetadataDefinitions(endeavorTypeId);
      if (!metadataResponse.ok) {
        throw new Error('Failed to load metadata definitions');
      }
      const metadataData = await metadataResponse.json();
      console.log('EndeavorTypeMetadataList: Raw API response:', metadataData);
      setMetadataDefinitions(Array.isArray(metadataData) ? metadataData : []);
    } catch (error) {
      console.error('Error loading endeavor type metadata:', error);
      setError('Failed to load endeavor type metadata. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [endeavorTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddMetadata = () => {
    navigate(`/settings/endeavor-types/${endeavorTypeId}/metadata/create`);
  };

  const handleEditMetadata = (relationshipId) => {
    navigate(`/settings/endeavor-types/${endeavorTypeId}/metadata/${relationshipId}/edit`);
  };

  const handleDeleteMetadata = async (relationshipId, fieldName) => {
    if (!window.confirm(`Are you sure you want to remove the "${fieldName}" metadata field from this endeavor type? This action cannot be undone.`)) {
      return;
    }

    try {
      // UPDATED: Use the relationship ID for deletion
      const response = await EndeavorTypeApi.deleteEndeavorTypeMetadataDefinition(endeavorTypeId, relationshipId);
      if (!response.ok) {
        throw new Error('Failed to delete metadata definition');
      }
      // Reload the data
      loadData();
    } catch (error) {
      console.error('Error deleting metadata definition:', error);
      setError('Failed to delete metadata definition. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading endeavor type metadata...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/settings/endeavor-types" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
          ← Back to Endeavor Types
        </Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2>Metadata Fields for {endeavorType?.name || 'Endeavor Type'}</h2>
          <p style={{ color: '#666', margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
            Configure custom metadata fields for this endeavor type
          </p>
        </div>
        <button 
          type="button" 
          onClick={handleAddMetadata}
          className="btn-primary"
          style={{
            padding: '0.5rem 1rem'
          }}
        >
          Add Metadata Field
        </button>
      </div>
      
      {error && (
        <div className="error-message" style={{ 
          color: '#dc3545', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {metadataDefinitions.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#666',
          backgroundColor: 'var(--semantic-surface-panel, #f9f9f9)',
          borderRadius: '4px'
        }}>
          <h3>No Metadata Fields</h3>
          <p>This endeavor type doesn't have any custom metadata fields yet.</p>
          <button 
            type="button" 
            onClick={handleAddMetadata}
            className="btn-primary"
            style={{
              padding: '0.5rem 1rem',
              marginTop: '1rem'
            }}
          >
            Add Your First Metadata Field
          </button>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Field Name</th>
              <th>Field Type</th>
              <th>Required</th>
              <th>Display Order</th>
              <th>Default Value</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {metadataDefinitions.map((relationship) => {
              // UPDATED: Handle the correct API response structure
              const relationshipId = relationship.id;
              const metadata = relationship.metadataDefinition;
              
              if (!metadata) {
                console.warn('EndeavorTypeMetadataList: Missing metadataDefinition in relationship:', relationship);
                return null;
              }
              
              return (
                <tr key={relationshipId}>
                  <td>
                    <strong>{metadata.name || 'Unnamed Field'}</strong>
                    {metadata.description && (
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {metadata.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'var(--badge-neutral-bg, #e9ecef)',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      {metadata.fieldType || 'TEXT'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      color: relationship.isRequired ? '#28a745' : '#6c757d',
                      fontWeight: '500'
                    }}>
                      {relationship.isRequired ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{relationship.ordering || 1}</td>
                  <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {metadata.defaultValue || '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleEditMetadata(relationshipId)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#007bff', 
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          padding: '0'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteMetadata(relationshipId, metadata.name)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#dc3545', 
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          padding: '0'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'var(--semantic-surface-panel, #f8f9fa)', borderRadius: '4px' }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>About Metadata Fields</h4>
        <p style={{ margin: '0', fontSize: '0.875rem', color: '#6c757d' }}>
          Metadata fields allow you to collect additional information when creating endeavors of this type. 
          For example, a "Course" endeavor type might have fields for "Subject Area", "Credit Hours", 
          "Instructor", etc. These fields help organize and categorize your learning activities.
        </p>
      </div>
    </div>
  );
}

export default EndeavorTypeMetadataList;